import {useContext, useEffect, useMemo} from "react";
import {useQuery} from "@tanstack/react-query";
import {ArrowUpRight, Check2, LightningChargeFill} from "react-bootstrap-icons";
import {GlobalContext, ReducerActionType} from "../../GlobalReducer";
import {ReducerStateInterface} from "../../interfaces/core/ReducerStateInterface";
import {ProductInterface} from "../../interfaces/catalog/ProductInterface";
import {getProductsByCategory} from "../../api/catalog";

const CATALOG_CODE = "002001001";

type ReviewItem = {
    rank: number;
    articul: string;
    brand: string;
    title: string;
    verdict: string;
    maxTorque: number;
    workingTorque: number;
    rpm: number;
    weight: string;
    mechanism: string;
    paragraphs: string[];
    pros: string[];
    cons: string[];
    fallback: {
        id: number;
        humanUrl: string;
        image: string;
    };
};

const reviewItems: ReviewItem[] = [
    {
        rank: 1,
        articul: "AIW1275",
        brand: "Thorvik",
        title: "Лучший по соотношению характеристик",
        verdict: "Выбор редакции",
        maxTorque: 1275,
        workingTorque: 900,
        rpm: 8000,
        weight: "2,1",
        mechanism: "двойной молоток",
        paragraphs: [
            "Thorvik AIW1275 развивает до 1275 Н·м, а заявленный рабочий момент составляет 900 Н·м. При этом инструмент весит около 2,1 кг и работает на скорости до 8000 об/мин.",
            "Используется распространенный ударный механизм типа «двойной молоток». Корпус выполнен из композита, корпус ударного механизма — из алюминия.",
            "По совокупности характеристик AIW1275 выглядит одним из наиболее интересных вариантов в подборке. Он лишь немного уступает Jonnesway JAI-1114 по максимальному моменту, но находится среди лидеров по мощности и при этом заметно доступнее многих конкурентов."
        ],
        pros: ["высокий момент", "сравнительно небольшая масса", "композитный корпус", "сильное соотношение цены и характеристик"],
        cons: ["рабочий момент ниже, чем у AIW12122 и OMP11212"],
        fallback: {
            id: 1045018585,
            humanUrl: "aiw1275-gaykovert-udarniyy-pnevmaticheskiy-12dr-8000-obmin-1275-nm-thorvik",
            image: "/public/images/catalog-resize/400x400/thorvik/10802/list/1.jpg"
        }
    },
    {
        rank: 2,
        articul: "JAI-1114",
        brand: "Jonnesway",
        title: "Максимальная мощность в компактном формате",
        verdict: "Самый мощный",
        maxTorque: 1356,
        workingTorque: 880,
        rpm: 9500,
        weight: "2,3",
        mechanism: "двойной молоток",
        paragraphs: [
            "JAI-1114 — самый мощный из рассматриваемых гайковертов. Производитель заявляет максимальный момент 1356 Н·м и рабочий 880 Н·м.",
            "Еще одна сильная сторона — скорость свободного вращения 9500 об/мин, максимальная в этой подборке. Композитный корпус помогает удерживать массу инструмента на уровне 2,3 кг.",
            "Модель интересна прежде всего тем, кому нужен большой запас максимального момента без перехода к тяжелым гайковертам с квадратом 3/4 или 1 дюйм."
        ],
        pros: ["максимальный момент 1356 Н·м", "9500 об/мин", "композитный корпус", "умеренная масса"],
        cons: ["высокая стоимость", "по рабочему моменту уступает нескольким моделям"],
        fallback: {
            id: 179716263,
            humanUrl: "jai-1114-gaykovert-pnevmaticheskiy-udarniyy-kompozitniyy-12dr-9500-obmin-1356-nm-jonnesway",
            image: "/public/images/catalog-resize/400x400/jonnesway/8930/zoom/1.jpg"
        }
    },
    {
        rank: 3,
        articul: "AIW12122",
        brand: "Thorvik",
        title: "Лидер по рабочему моменту",
        verdict: "Для тугого крепежа",
        maxTorque: 1220,
        workingTorque: 1010,
        rpm: 8800,
        weight: "2,7",
        mechanism: "двойной молоток",
        paragraphs: [
            "Максимальный момент AIW12122 составляет 1220 Н·м, однако гораздо интереснее показатель рабочего момента — 1010 Н·м. Это максимальное значение среди всех восьми моделей.",
            "Скорость вращения достигает 8800 об/мин. Используется механизм «двойной молоток», а корпус и ударный узел выполнены преимущественно из металла.",
            "Обратной стороной мощности становится масса: 2,7 кг. Для эпизодической работы разница невелика, но при многочасовой эксплуатации более легкие модели могут быть удобнее."
        ],
        pros: ["максимальный в подборке рабочий момент", "высокая скорость вращения", "большой запас мощности"],
        cons: ["заметно тяжелее компактных AIW1210C и JAI-0964"],
        fallback: {
            id: 431476068,
            humanUrl: "aiw12122-gaykovert-udarniyy-pnevmaticheskiy-12dr-8800-obmin-1220-nm-thorvik",
            image: "/public/images/catalog-resize/400x400/thorvik/9463/foto/1.jpg"
        }
    },
    {
        rank: 4,
        articul: "OMP11212",
        brand: "Ombra",
        title: "Мощный универсальный вариант",
        verdict: "Баланс для сервиса",
        maxTorque: 1200,
        workingTorque: 960,
        rpm: 7000,
        weight: "2,7",
        mechanism: "двойной молоток",
        paragraphs: [
            "OMP11212 развивает максимум 1200 Н·м и имеет рабочий момент 960 Н·м. По второму показателю он уступает только Thorvik AIW12122.",
            "Скорость свободного вращения — 7000 об/мин, масса — около 2,7 кг. Ударный механизм традиционный для этого класса — Twin Hammer.",
            "Модель занимает интересное положение между особо мощными и универсальными гайковертами. Запаса момента достаточно для тяжелых соединений, при этом инструмент остается в стандартном форм-факторе 1/2″."
        ],
        pros: ["рабочий момент 960 Н·м", "максимальные 1200 Н·м", "металлический ударный узел"],
        cons: ["сравнительно высокая масса", "меньшие обороты относительно конкурентов"],
        fallback: {
            id: 93550,
            humanUrl: "omp11212-gaykovert-pnevmaticheskiy-udarniyy-12dr-7000-obmin-1200-nm-ombra",
            image: "/public/images/catalog-resize/400x400/ombra/2611/list/1.jpg"
        }
    },
    {
        rank: 5,
        articul: "AIW1210C",
        brand: "Thorvik",
        title: "Легкий и компактный",
        verdict: "Самый легкий",
        maxTorque: 1000,
        workingTorque: 700,
        rpm: 8500,
        weight: "1,2",
        mechanism: "двойной молоток",
        paragraphs: [
            "Главное преимущество AIW1210C — сочетание мощности и массы. Максимальный момент составляет 1000 Н·м, рабочий — 700 Н·м, тогда как масса нетто заявлена всего 1,2 кг.",
            "Корпус имеет компактное исполнение, длина составляет около 179 мм. Скорость свободного вращения достигает 8500 об/мин.",
            "По максимальному моменту модель уступает лидерам, но для большого количества стандартных слесарных и шиномонтажных операций 1000 Н·м — уже серьезный показатель."
        ],
        pros: ["масса всего 1,2 кг", "компактный корпус", "8500 об/мин", "максимальный момент 1000 Н·м"],
        cons: ["рабочий момент ниже, чем у большинства мощных моделей рейтинга"],
        fallback: {
            id: 1235395289,
            humanUrl: "aiw1210c-gaykovert-udarniyy-pnevmaticheskiy-12dr-8500-obmin-1000-nm-kompakt-thorvik",
            image: "/public/images/catalog-resize/400x400/thorvik/11442/list/1.jpg"
        }
    },
    {
        rank: 6,
        articul: "JAI-0964",
        brand: "Jonnesway",
        title: "Легкий профессиональный вариант",
        verdict: "Для регулярной работы",
        maxTorque: 930,
        workingTorque: 740,
        rpm: 7500,
        weight: "1,8",
        mechanism: "двойной молоток",
        paragraphs: [
            "JAI-0964 имеет максимальный момент 930 Н·м и рабочий 740 Н·м. Скорость вращения без нагрузки составляет 7500 об/мин.",
            "При массе около 1,8 кг это один из наиболее легких полноразмерных гайковертов рейтинга. Корпус выполнен из композита, ударный механизм — Twin Hammer.",
            "Характеристики делают JAI-0964 интересным для регулярной работы, где важнее баланс массы и достаточного момента, чем абсолютный максимум мощности."
        ],
        pros: ["масса 1,8 кг", "композитный корпус", "профессиональная конструкция"],
        cons: ["высокая цена относительно моделей с большим заявленным моментом"],
        fallback: {
            id: 91818,
            humanUrl: "jai-0964-gaykovert-pnevmaticheskiy-udarniyy-kompozitniyy-12dr-7500-obmin-930-nm-jonnesway",
            image: "/public/images/catalog-resize/400x400/jonnesway/6036/list/1.jpg"
        }
    },
    {
        rank: 7,
        articul: "JAI-1054",
        brand: "Jonnesway",
        title: "Универсальная модель среднего класса",
        verdict: "Классический вариант",
        maxTorque: 920,
        workingTorque: 780,
        rpm: 7000,
        weight: "2,97",
        mechanism: "двойной молоток",
        paragraphs: [
            "JAI-1054 развивает до 920 Н·м с рабочим моментом 780 Н·м. Скорость свободного вращения — 7000 об/мин.",
            "Корпус изготовлен из алюминия, ударный механизм — двойной молоток. Средний циклический расход воздуха заявлен на уровне 119 л/мин.",
            "По абсолютным характеристикам модель не претендует на лидерство, однако находится в типичном для профессионального гайковерта диапазоне мощности."
        ],
        pros: ["рабочий момент 780 Н·м", "умеренный расход воздуха", "классическая конструкция"],
        cons: ["почти 3 кг массы", "нет преимущества по мощности перед более доступными моделями"],
        fallback: {
            id: 91854,
            humanUrl: "jai-1054-gaykovert-pnevmaticheskiy-udarniyy-12dr-7000-obmin-920-nm-jonnesway",
            image: "/public/images/catalog-resize/400x400/jonnesway/4946/list/1.jpg"
        }
    },
    {
        rank: 8,
        articul: "OMP11281",
        brand: "Ombra",
        title: "Базовый вариант для стандартных задач",
        verdict: "Для умеренных нагрузок",
        maxTorque: 815,
        workingTorque: 750,
        rpm: 8000,
        weight: "2,63",
        mechanism: "двойной молоток",
        paragraphs: [
            "OMP11281 — наименее мощный участник сравнения по максимальному показателю: 815 Н·м. Однако рабочий момент составляет 750 Н·м, то есть разница с некоторыми более мощными моделями не столь велика.",
            "Инструмент вращается со скоростью до 8000 об/мин и потребляет в среднем около 120 л/мин воздуха. Масса — 2,63 кг.",
            "Это вариант прежде всего для тех случаев, когда экстремальный запас момента не требуется и основная работа приходится на стандартный автомобильный крепеж."
        ],
        pros: ["достойный рабочий момент", "8000 об/мин", "умеренный расход воздуха"],
        cons: ["самый небольшой максимальный момент среди участников"],
        fallback: {
            id: 93586,
            humanUrl: "omp11281-gaykovert-pnevmaticheskiy-udarniyy-12dr-8000-obmin-815-nm-ombra",
            image: "/public/images/catalog-resize/400x400/ombra/2610/list/1.jpg"
        }
    }
];

const formatNumber = (value: number) => new Intl.NumberFormat("ru-RU").format(value);
const formatPrice = (amount?: number) => amount
    ? `${new Intl.NumberFormat("ru-RU").format(amount / 100)} ₽`
    : null;

function getProductUrl(item: ReviewItem, product?: ProductInterface) {
    return `/catalog/item/${CATALOG_CODE}/${product?.id || item.fallback.id}/${product?.human_url || item.fallback.humanUrl}`;
}

function getProductImage(item: ReviewItem, product?: ProductInterface) {
    return `https://www.pride.ru${product?.list_image || item.fallback.image}`;
}

export default function PneumaticWrenchesReview() {
    const [state, dispatch] = useContext<[ReducerStateInterface, Function]>(GlobalContext);
    const {data} = useQuery({
        queryKey: ["review-products", CATALOG_CODE, state.customer?.id],
        queryFn: () => getProductsByCategory(CATALOG_CODE, state.customer)
    });

    const products = useMemo(() => {
        const map = new Map<string, ProductInterface>();
        data?.products?.forEach(product => map.set(product.articul, product));
        return map;
    }, [data]);

    useEffect(() => {
        dispatch({
            type: ReducerActionType.SET_BREADCRUMBS,
            payload: [{name: "Обзоры"}, {name: "Пневматические гайковерты 1/2″"}]
        });
        dispatch({
            type: ReducerActionType.SET_PARAM,
            paramName: "meta",
            payload: {
                title: "Лучшие пневматические гайковерты 1/2″ — рейтинг 8 моделей",
                html_header: "Лучшие пневматические гайковерты 1/2″",
                description: "Сравнение 8 пневматических гайковертов Thorvik, Jonnesway и Ombra: момент, масса, скорость, плюсы и минусы.",
                image: "https://www.pride.ru" + reviewItems[0].fallback.image
            }
        });

        return () => {
            dispatch({type: ReducerActionType.SET_BREADCRUMBS, payload: []});
        };
    }, [dispatch]);

    return (
        <article className="wrench-review">
            <div className="container">
                <header className="wrench-review__hero">
                    <div className="wrench-review__hero-copy">
                        <div className="wrench-review__eyebrow">
                            <LightningChargeFill aria-hidden="true"/> Обзор и сравнение
                        </div>
                        <h1>Лучшие пневматические гайковерты 1/2″</h1>
                        <p className="wrench-review__lead">
                            Сравниваем 8 ударных моделей Thorvik, Jonnesway и Ombra для автосервиса,
                            шиномонтажа и гаража — от компактных 1,2 кг до самых мощных 1356 Н·м.
                        </p>
                        <div className="wrench-review__hero-metrics" aria-label="Параметры обзора">
                            <div><strong>8</strong><span>моделей</span></div>
                            <div><strong>815–1356</strong><span>Н·м максимум</span></div>
                            <div><strong>1/2″</strong><span>посадочный квадрат</span></div>
                        </div>
                    </div>
                    <a className="wrench-review__hero-product" href={getProductUrl(reviewItems[0], products.get("AIW1275"))}>
                        <span className="wrench-review__hero-label">Выбор обзора</span>
                        <img src={getProductImage(reviewItems[0], products.get("AIW1275"))} alt="Пневматический гайковерт Thorvik AIW1275"/>
                        <span className="wrench-review__hero-model">Thorvik <b>AIW1275</b></span>
                        <span className="wrench-review__hero-link">Открыть товар <ArrowUpRight aria-hidden="true"/></span>
                    </a>
                </header>

                <section className="wrench-review__intro">
                    <p>
                        Формат 1/2 дюйма особенно распространен в автосервисах и мастерских: такие
                        инструменты остаются достаточно компактными, но развивают момент, которого хватает
                        для большинства работ с автомобильным крепежом.
                    </p>
                    <p>
                        При отборе мы учитывали не только максимальный и рабочий момент, но также массу,
                        обороты, конструкцию ударного механизма, расход воздуха и стоимость.
                    </p>
                </section>

                <section className="wrench-review__section" aria-labelledby="quick-rating-title">
                    <div className="wrench-review__section-head">
                        <span>01</span>
                        <div>
                            <p>Сразу к результатам</p>
                            <h2 id="quick-rating-title">Краткий рейтинг</h2>
                        </div>
                    </div>
                    <div className="wrench-review__rating-grid">
                        {reviewItems.map(item => {
                            const product = products.get(item.articul);
                            return (
                                <a className={`wrench-review__rating-card ${item.rank === 1 ? "is-winner" : ""}`}
                                   href={`#${item.articul.toLowerCase()}`} key={item.articul}>
                                    <span className="wrench-review__rank">{String(item.rank).padStart(2, "0")}</span>
                                    <span className="wrench-review__rating-copy">
                                        <small>{item.verdict}</small>
                                        <strong>{item.brand} {item.articul}</strong>
                                        <span>{item.maxTorque} Н·м · {item.weight} кг</span>
                                    </span>
                                    <img src={getProductImage(item, product)} alt="" loading="lazy"/>
                                </a>
                            );
                        })}
                    </div>
                </section>

                <section className="wrench-review__section" aria-labelledby="comparison-title">
                    <div className="wrench-review__section-head">
                        <span>02</span>
                        <div>
                            <p>Все цифры рядом</p>
                            <h2 id="comparison-title">Сравнение характеристик</h2>
                        </div>
                    </div>
                    <div className="wrench-review__table-wrap">
                        <table className="wrench-review__table">
                            <thead>
                            <tr>
                                <th>Модель</th>
                                <th>Max момент</th>
                                <th>Рабочий</th>
                                <th>Скорость</th>
                                <th>Масса</th>
                                <th>Механизм</th>
                            </tr>
                            </thead>
                            <tbody>
                            {reviewItems.map(item => (
                                <tr key={item.articul}>
                                    <td><a href={`#${item.articul.toLowerCase()}`}>{item.brand} <b>{item.articul}</b></a></td>
                                    <td><strong>{formatNumber(item.maxTorque)} Н·м</strong></td>
                                    <td>{formatNumber(item.workingTorque)} Н·м</td>
                                    <td>{formatNumber(item.rpm)} об/мин</td>
                                    <td>{item.weight} кг</td>
                                    <td>{item.mechanism}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="wrench-review__section" aria-labelledby="details-title">
                    <div className="wrench-review__section-head">
                        <span>03</span>
                        <div>
                            <p>Подробный разбор</p>
                            <h2 id="details-title">Восемь лучших моделей</h2>
                        </div>
                    </div>

                    <div className="wrench-review__products">
                        {reviewItems.map(item => {
                            const product = products.get(item.articul);
                            const price = formatPrice(product?.client_price?.amount || product?.current_price?.amount);
                            return (
                                <section className="wrench-review__product" id={item.articul.toLowerCase()} key={item.articul}>
                                    <div className="wrench-review__product-visual">
                                        <span className="wrench-review__product-rank">#{item.rank}</span>
                                        <img src={getProductImage(item, product)} alt={`${item.brand} ${item.articul}`} loading="lazy"/>
                                        <div className="wrench-review__product-specs">
                                            <span><b>{formatNumber(item.maxTorque)}</b> Н·м max</span>
                                            <span><b>{item.weight}</b> кг</span>
                                            <span><b>{formatNumber(item.rpm)}</b> об/мин</span>
                                        </div>
                                    </div>
                                    <div className="wrench-review__product-content">
                                        <div className="wrench-review__product-heading">
                                            <div>
                                                <span className="wrench-review__badge">{item.verdict}</span>
                                                <h3>{item.rank}. {item.brand} {item.articul}</h3>
                                                <p className="wrench-review__product-subtitle">{item.title}</p>
                                            </div>
                                            <span className="wrench-review__brand">{item.brand}</span>
                                        </div>
                                        <div className="wrench-review__product-text">
                                            {item.paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
                                        </div>
                                        <div className="wrench-review__pros-cons">
                                            <div>
                                                <strong>Плюсы</strong>
                                                <ul>{item.pros.map(pro => <li key={pro}><Check2 aria-hidden="true"/>{pro}</li>)}</ul>
                                            </div>
                                            <div className="is-cons">
                                                <strong>Минусы</strong>
                                                <ul>{item.cons.map(con => <li key={con}><span>−</span>{con}</li>)}</ul>
                                            </div>
                                        </div>
                                        <div className="wrench-review__product-cta">
                                            <a className="btn btn-primary big" href={getProductUrl(item, product)}>
                                                Перейти к товару <ArrowUpRight aria-hidden="true"/>
                                            </a>
                                            {price && <span><small>Цена на сайте</small><b>{price}</b></span>}
                                            {product && <span className={product.has_stock ? "is-available" : ""}>
                                                {product.has_stock ? product.smart_stock_label : "нет в наличии"}
                                            </span>}
                                        </div>
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                </section>

                <section className="wrench-review__choice" aria-labelledby="choice-title">
                    <div className="wrench-review__section-head wrench-review__section-head--light">
                        <span>04</span>
                        <div>
                            <p>Финальный выбор</p>
                            <h2 id="choice-title">Какой гайковерт выбрать</h2>
                        </div>
                    </div>
                    <div className="wrench-review__choice-grid">
                        <p><b>Максимальная мощность</b><span>Jonnesway JAI-1114</span><strong>1356 Н·м</strong></p>
                        <p><b>Рабочий момент</b><span>Thorvik AIW12122</span><strong>1010 Н·м</strong></p>
                        <p><b>Минимальная масса</b><span>Thorvik AIW1210C</span><strong>1,2 кг</strong></p>
                        <p className="is-main"><b>Лучший баланс</b><span>Thorvik AIW1275</span><strong>выбор обзора</strong></p>
                    </div>
                    <p className="wrench-review__choice-note">
                        При выборе учитывайте производительность компрессора, давление в пневмомагистрали,
                        диаметр шланга и качество подготовки воздуха. Для окончательной затяжки ответственных
                        соединений используйте динамометрический ключ, а не ориентируйтесь на момент гайковерта.
                    </p>
                </section>
            </div>
        </article>
    );
}
